import { useEffect, useState } from 'react';
import { formatMoney } from '@domain';
import {
  loadDocumentDetail,
  loadPaymentAccounts,
  recordPayment,
  type PaymentAccounts,
} from '../api.js';
import type { DataSource } from '../api.js';
import type { Lang, Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';
import { accountTypeLabel } from '../data/accounts.js';
import type { XeroDocument } from './printTypes.js';

/**
 * One document, as Xero holds it, with a way to pay it.
 *
 * WHY THE PAYMENT IS HERE AND NOT ONLY ON ITS OWN SCREEN
 * The separate screen starts by choosing an invoice from a list of everything
 * outstanding, which is right when somebody sits down with a bank statement and
 * works through it. It is the wrong shape when the document is already open and
 * the question is simply "this one, now" — and it is the shape Xero itself
 * uses, so it is what people already know.
 *
 * The accounts are Xero's own, narrowed to those it will actually accept a
 * payment into, so what is posted lands where the ledger expects it.
 */

const TITLES: Record<string, { en: string; ar: string }> = {
  invoice: { en: 'Invoice', ar: 'فاتورة مبيعات' },
  bill: { en: 'Bill', ar: 'فاتورة مشتريات' },
  quote: { en: 'Quote', ar: 'عرض سعر' },
  po: { en: 'Purchase order', ar: 'أمر شراء' },
};

/** Only these can be paid. A quote is not a debt and a PO is not yet one. */
const PAYABLE = new Set(['invoice', 'bill']);

/** Xero's own words for where a document stands, in both languages. */
const STATUS: Record<string, { en: string; ar: string }> = {
  DRAFT: { en: 'Draft', ar: 'مسودة' },
  SUBMITTED: { en: 'Awaiting approval', ar: 'بانتظار الاعتماد' },
  AUTHORISED: { en: 'Awaiting payment', ar: 'بانتظار السداد' },
  PAID: { en: 'Paid', ar: 'مدفوعة' },
  VOIDED: { en: 'Voided', ar: 'ملغاة' },
  DELETED: { en: 'Deleted', ar: 'محذوفة' },
};

export function DocumentScreen({
  kind,
  id,
  source,
  isAdmin,
  lang,
  t,
  onBack,
  onPrint,
}: {
  kind: string;
  id: string;
  source: DataSource;
  isAdmin: boolean;
  lang: Lang;
  t: Translate;
  onBack: () => void;
  /** Our own bilingual layout, with the branding set in this product. */
  onPrint: () => void;
}) {
  const rtl = lang === 'ar';
  const [doc, setDoc] = useState<XeroDocument | null>(null);
  const [currency, setCurrency] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [options, setOptions] = useState<PaymentAccounts | null>(null);
  /*
    Still fetching, answered, or refused.

    Three states that used to render as one word — "Choose an account" —
    whether the list had ten accounts in it, had not arrived yet, or had
    come back empty. Somebody looking at an empty dropdown could not tell
    whether their bank accounts were missing from Xero or merely still on
    their way, and neither could anybody they asked.
  */
  const [accountsState, setAccountsState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [accountCode, setAccountCode] = useState('');
  const [method, setMethod] = useState('');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    loadDocumentDetail(kind, id, source)
      .then((detail) => {
        if (cancelled) return;
        const record = detail.document as XeroDocument;
        setDoc(record);
        // The document's own currency, not the company's base one. A bill in
        // USD is in USD, and labelling its total QAR would be wrong by whatever
        // the rate happens to be that day.
        setCurrency(record.CurrencyCode ?? (detail as { currency?: string }).currency ?? '');
        // Offering the outstanding amount saves typing and makes paying in
        // full the easy path, which is what nearly every payment is.
        setAmount(String(record.AmountDue ?? ''));
      })
      .catch(() => !cancelled && setError('That document could not be read from Xero.'));
    return () => {
      cancelled = true;
    };
  }, [kind, id, source, reload]);

  useEffect(() => {
    if (!isAdmin || !PAYABLE.has(kind)) return;
    let cancelled = false;
    setAccountsState('loading');
    loadPaymentAccounts(source)
      .then((loaded) => {
        if (cancelled) return;
        setOptions(loaded);
        setAccountsState('ready');
        setMethod((current) => current || loaded.defaultMethod || loaded.methods[0]?.code || '');
      })
      .catch(() => {
        if (cancelled) return;
        setOptions(null);
        setAccountsState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, kind, source]);

  const title = TITLES[kind] ?? { en: 'Document', ar: 'مستند' };
  const lines = doc?.LineItems ?? [];
  const due = doc?.AmountDue ?? 0;
  const status = doc?.Status ?? '';
  const statusWords = STATUS[status] ?? { en: status, ar: status };

  const typed = Number(amount.replace(/[^\d.]/g, ''));
  const overpaying = Number.isFinite(typed) && typed > due + 0.005;
  const ready =
    accountCode !== '' && method !== '' && amount.trim() !== '' && typed > 0 && !busy;

  /* Bank accounts first, because that is what nearly every payment uses
     and a chart can carry other payment-enabled accounts behind them. */
  const payableAccounts = [...(options?.accounts ?? [])].sort((a, b) =>
    a.type === b.type ? 0 : a.type === 'BANK' ? -1 : b.type === 'BANK' ? 1 : 0,
  );

  // A bill leaves the bank; an invoice arrives in it. Xero labels these
  // differently and so should we, or somebody reconciling will read it wrong.
  const accountLabel = kind === 'bill' ? t('Paid from') : t('Paid into');

  const submit = async () => {
    setBusy(true);
    setPaymentError(null);
    try {
      await recordPayment({ invoiceId: id, accountCode, method, date: paidOn, amount, reference });
      setDone(true);
      setReference('');
      // Re-read rather than guess: Xero decides what is now outstanding, and
      // whether the document has become PAID.
      setReload((n) => n + 1);
    } catch (caught) {
      setPaymentError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const money = (value: number | undefined) => (value === undefined ? '—' : formatMoney(value));

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '980px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onBack}
          style={{ background: 'none', border: 0, color: theme.ink, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
        >
          {rtl ? t('→ Back') : t('← Back')}
        </button>
        <div style={{ flex: 1 }} />
        <a
          href={`/api/documents/${kind}/${id}/pdf`}
          target="_blank"
          rel="noreferrer"
          title={t('The layout Xero makes, not the branding set here')}
          style={{
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '8px 14px',
            fontSize: '12.5px',
            fontWeight: 600,
            color: theme.text,
            textDecoration: 'none',
          }}
        >
          {t('Xero PDF')}
        </a>

        {/*
          Two different documents, and the difference was invisible from here.

          "Xero PDF" is the file Xero makes, using the branding theme set
          inside Xero — it owes nothing to the logo, colours or bank details
          configured in this product. Our own layout was reachable only from a
          small printer icon on the list, which is not where somebody is
          standing when they want to send the invoice they are looking at.

          Both are offered, and each says which it is.
        */}
        <button
          onClick={onPrint}
          title={t('Our own bilingual layout, with the branding set in this product')}
          style={{
            border: 0,
            borderRadius: theme.radiusControl,
            padding: '8px 14px',
            fontSize: '12.5px',
            fontWeight: 600,
            background: theme.nav,
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          {t('Print or save as PDF')}
        </button>
      </div>

      {error && (
        <div style={{ color: theme.badDeep, fontWeight: 600, fontSize: '13px' }}>{t(error)}</div>
      )}

      {doc && (
        <>
          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '14px',
                flexWrap: 'wrap',
                padding: '14px 18px',
                borderBottom: `1px solid ${theme.dividerSoft}`,
                background: theme.surfaceHeader,
              }}
            >
              <span style={{ fontSize: '15px', fontWeight: 700 }}>
                {rtl ? title.ar : title.en} {doc.InvoiceNumber ?? ''}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: status === 'PAID' ? theme.good : theme.textMuted,
                }}
              >
                {rtl ? statusWords.ar : statusWords.en}
              </span>
              <div style={{ flex: 1 }} />
              <span dir="ltr" style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '15px' }}>
                {money(doc.Total)} {currency}
              </span>
            </div>

            <div style={{ padding: '16px 18px', display: 'flex', gap: '30px', flexWrap: 'wrap', fontSize: '12.5px' }}>
              <Fact label={kind === 'bill' ? t('From') : t('To')} value={doc.Contact?.Name ?? '—'} />
              <Fact label={t('Date')} value={(doc.DateString ?? '').slice(0, 10) || '—'} mono />
              <Fact label={t('Due')} value={(doc.DueDateString ?? '').slice(0, 10) || '—'} mono />
              <Fact label={t('Reference')} value={doc.Reference || '—'} />
            </div>

            <div style={{ padding: '0 18px 18px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.borderStrong}` }}>
                    <Th align="start">{t('Description')}</Th>
                    <Th>{t('Account')}</Th>
                    <Th align="end">{t('Qty')}</Th>
                    <Th align="end">{t('Unit price')}</Th>
                    <Th align="end">{t('Amount')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                      <td style={{ padding: '9px 6px' }}>{line.Description ?? ''}</td>
                      <td style={{ padding: '9px 6px', color: theme.textMuted }}>{line.AccountCode ?? ''}</td>
                      <td dir="ltr" style={{ padding: '9px 6px', textAlign: 'end', fontFamily: theme.fontMono }}>
                        {line.Quantity ?? ''}
                      </td>
                      <td dir="ltr" style={{ padding: '9px 6px', textAlign: 'end', fontFamily: theme.fontMono }}>
                        {money(line.UnitAmount)}
                      </td>
                      <td dir="ltr" style={{ padding: '9px 6px', textAlign: 'end', fontFamily: theme.fontMono }}>
                        {money(line.LineAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                <table style={{ minWidth: '260px', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                  <tbody>
                    <Total label={t('Subtotal')} value={doc.SubTotal} currency={currency} />
                    <Total label={t('Tax')} value={doc.TotalTax} currency={currency} />
                    <Total label={t('Total')} value={doc.Total} currency={currency} strong />
                    {(doc.AmountPaid ?? 0) > 0 && (
                      <Total label={t('Amount paid')} value={doc.AmountPaid} currency={currency} />
                    )}
                    <Total label={t('Amount due')} value={doc.AmountDue} currency={currency} strong />
                  </tbody>
                </table>
              </div>
            </div>
          </Card>

          {/*
            Only where there is something to pay, and only for CFOAI. A client
            saying their customer paid them is information; a ledger entry is
            not theirs to make.
          */}
          {isAdmin && PAYABLE.has(kind) && due > 0.005 && (
            <>
              <div style={{ height: '18px' }} />
              <Card>
                <div
                  style={{
                    padding: '12px 18px',
                    borderBottom: `1px solid ${theme.dividerSoft}`,
                    fontWeight: 600,
                    fontSize: '13px',
                    background: theme.surfaceHeader,
                  }}
                >
                  {t('Make a payment')}
                </div>

                <div style={{ padding: '16px 18px', display: 'grid', gap: '14px' }}>
                  <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <Field label={t('Amount')} width="150px">
                      <input
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value);
                          setDone(false);
                        }}
                        dir="ltr"
                        style={input}
                      />
                    </Field>

                    <Field label={t('Date paid')} width="160px">
                      <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} style={input} />
                    </Field>

                    <Field label={accountLabel} width="240px">
                      <select
                        value={accountCode}
                        onChange={(e) => setAccountCode(e.target.value)}
                        disabled={payableAccounts.length === 0}
                        style={input}
                      >
                        <option value="">
                          {accountsState === 'loading' ? t('Reading from Xero…')
                          : accountsState === 'failed' ? t('Xero could not be reached')
                          : payableAccounts.length === 0 ? t('No account in Xero can receive payments')
                          : t('Choose an account')}
                        </option>
                        {/*
                          Xero's own bank accounts, and any other account
                          explicitly set to receive payments. The type is
                          shown because "which of these is my bank?" is the
                          question somebody is actually asking here.
                        */}
                        {payableAccounts.map((account) => (
                          <option key={account.code} value={account.code}>
                            {account.code} — {account.name}
                            {account.type ? ` · ${t(accountTypeLabel(account.type))}` : ''}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label={t('Reference')} width="190px">
                      <input value={reference} onChange={(e) => setReference(e.target.value)} style={input} />
                    </Field>
                  </div>

                  <div style={{ display: 'grid', gap: '7px' }}>
                    <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>
                      {t('How it was paid')}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {(options?.methods ?? []).map((option) => {
                        const picked = option.code === method;
                        return (
                          <button
                            key={option.code}
                            type="button"
                            onClick={() => setMethod(option.code)}
                            style={{
                              background: picked ? theme.nav : 'transparent',
                              color: picked ? '#fff' : theme.text,
                              border: `1px solid ${picked ? theme.nav : theme.borderStrong}`,
                              borderRadius: theme.radiusControl,
                              padding: '7px 13px',
                              fontSize: '12.5px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {rtl ? option.ar : option.en}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {overpaying && (
                    <div style={{ fontSize: '12px', color: theme.badDeep, fontWeight: 600 }}>
                      {t('That is more than is outstanding.')} {money(due)} {currency}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={!ready}
                      onClick={() => void submit()}
                      style={{
                        background: ready ? theme.good : theme.borderStrong,
                        color: '#fff',
                        border: 0,
                        borderRadius: theme.radiusControl,
                        padding: '11px 22px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: ready ? 'pointer' : 'default',
                      }}
                    >
                      {busy ? t('Recording…') : t('Record payment')}
                    </button>

                    {method !== '' && (
                      <span style={{ fontSize: '11.5px', color: theme.textMuted }}>
                        {t('Xero will show this as')}{' '}
                        <span dir="ltr" style={{ fontFamily: theme.fontMono }}>
                          {[options?.methods.find((m) => m.code === method)?.en, reference.trim()]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    )}

                    {paymentError && (
                      <span style={{ fontSize: '12px', color: theme.badDeep, fontWeight: 600 }}>{paymentError}</span>
                    )}
                  </div>
                </div>
              </Card>
            </>
          )}

          {done && due <= 0.005 && (
            <div style={{ marginTop: '14px', fontSize: '13px', fontWeight: 600, color: theme.good }}>
              {t('Paid in full.')}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const input: React.CSSProperties = {
  height: '36px',
  width: '100%',
  border: `1px solid ${theme.borderStrong}`,
  borderRadius: theme.radiusControl,
  padding: '0 10px',
  fontSize: '13px',
  fontFamily: 'inherit',
  background: theme.surface,
  color: 'inherit',
};

function Field({ label, width, children }: { label: string; width: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: '5px', width, minWidth: 0 }}>
      <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>{label}</span>
      {children}
    </label>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: '10.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: theme.textSubtle }}>
        {label}
      </div>
      <div style={mono ? { fontFamily: theme.fontMono } : undefined} dir={mono ? 'ltr' : undefined}>
        {value}
      </div>
    </div>
  );
}

function Th({ children, align = 'center' }: { children: React.ReactNode; align?: 'start' | 'end' | 'center' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '8px 6px',
        fontSize: '10.5px',
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: theme.textSubtle,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}

function Total({
  label,
  value,
  currency,
  strong,
}: {
  label: string;
  value: number | undefined;
  currency: string;
  strong?: boolean;
}) {
  if (value === undefined) return null;
  return (
    <tr style={strong ? { borderTop: `1px solid ${theme.borderStrong}` } : undefined}>
      <td style={{ padding: '5px 12px 5px 0', fontWeight: strong ? 700 : 400 }}>{label}</td>
      <td
        dir="ltr"
        style={{ padding: '5px 0', textAlign: 'end', fontFamily: theme.fontMono, fontWeight: strong ? 700 : 400 }}
      >
        {formatMoney(value)} {currency}
      </td>
    </tr>
  );
}
